import { useEffect } from 'react';
import { Constants } from '../../../Constants';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { RootState } from '../../app/store';
import { AgentTypingEntry, clearAgentTyping } from './agentTypingSlice';

/**
 * Customer-side receiver state for the "agent is typing" indicator (#1770). Reads the per-chat
 * entry from the agentTyping slice and runs the receiver-side safety auto-clear: if a "stopped
 * typing" signal is lost (network blip, agent tab closed mid-type), the indicator clears after
 * {@link Constants.liveChat.TYPING_RECEIVER_TIMEOUT_MS}. `ping` changes on every push (even a
 * repeated "true"), so the timer resets per ping instead of firing while the agent is still typing.
 *
 * Sole consumer is `ChatStatus` in both modes (#1933): it carries the agent-typing row in the
 * pinned slot above the composer — fully in legacy mode (DynamicReport off) and via the
 * `agentTypingOnly` prop in DynamicReport mode. `ChatStatusCard` no longer calls this hook, so only
 * one safety timer is ever mounted per chat.
 */
export const useAgentTypingIndicator = (chatId: string): boolean => {
    const dispatch = useAppDispatch();

    // Record index access is typed non-undefined, but a chat with no typing event yet has no
    // entry at runtime — narrow explicitly (mirrors the conversations lookup in signalRHubConnection).
    const entry = useAppSelector(
        (state: RootState) => state.agentTyping.typing[chatId] as AgentTypingEntry | undefined,
    );
    const isAgentTyping = entry?.isTyping ?? false;
    const ping = entry?.ping ?? 0;

    useEffect(() => {
        if (!isAgentTyping) {
            return;
        }
        const timer = setTimeout(() => {
            dispatch(clearAgentTyping({ chatId }));
        }, Constants.liveChat.TYPING_RECEIVER_TIMEOUT_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [isAgentTyping, ping, chatId, dispatch]);

    return isAgentTyping;
};
